import { useTranslation } from "@redotech/redo-i18n";
import DotsHorizontal from "@redotech/redo-icons/dots-horizontal.svg";
import { ObjectId } from "bson";
import classNames from "classnames";
import {
  createContext,
  memo,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Flex } from "../../flex";
import { SkeletonText } from "../../skeleton";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import { TextColorValue, TextWeightValue } from "../../theme/typography";
import { OverflowTooltip } from "../../tooltip/overflow-tooltip";
import { Tooltip } from "../../tooltip/tooltip";
import { RedoBadge, RedoBadgeProps } from "../badge/redo-badge";
import { RedoButton, RedoButtonProps } from "../buttons/redo-button";
import { CheckboxValue, RedoCheckbox } from "../checkbox/redo-checkbox";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "../command-menu/redo-command-menu";
import { Divider } from "../divider/divider";
import {
  RedoFeaturedIcon,
  RedoFeaturedIconProps,
} from "../featured-icon/redo-featured-icon";
import { RedoSpinner, RedoSpinnerSize } from "../loading/redo-spinner";
import * as redoTableCellsCss from "./redo-table-cells.module.css";
import * as redoTableRowCss from "./redo-table-row.module.css";

const CommandMenuContext = createContext<{
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
} | null>(null);

export function CommandMenuProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  return (
    <CommandMenuContext.Provider value={{ openMenuId, setOpenMenuId }}>
      {children}
    </CommandMenuContext.Provider>
  );
}

export interface RedoTableCellProps<DATA> {
  row: DATA;
  rowIndex: number;
}

/** Takes a way to convert rows into the usable data for the text cell, and returns a way to create a text cell */
interface TextCellProps {
  text: string;
  subtext?: string;
  subtext2?: string;
  leading?: boolean;
  textColor?: TextColorValue | undefined;
  fontWeight?: TextWeightValue;
  "data-arbiter"?: string;
}

type FeaturedIconTextCellProps = TextCellProps & {
  icon: {
    text: string;
    color: RedoFeaturedIconProps["color"];
    size: RedoFeaturedIconProps["size"];
    shape: RedoFeaturedIconProps["shape"];
  };
};

export const RedoTableTextCell = memo(function RedoTableTextCell({
  text,
  subtext,
  subtext2,
  leading,
  textColor,
  fontWeight,
  "data-arbiter": dataArbiter = "RedoTableTextCell",
}: TextCellProps) {
  const styleToArgs = isLeadingToArgsMap(leading, subtext);
  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
  const [subtextOverflowRef, setSubtextOverflowRef] =
    useState<HTMLDivElement | null>(null);
  const [subtext2OverflowRef, setSubtext2OverflowRef] =
    useState<HTMLDivElement | null>(null);
  return (
    <Flex
      className={classNames(
        redoTableCellsCss.overflowableTextContainer,
        redoTableCellsCss.cellContainer,
      )}
      data-arbiter={dataArbiter}
      dir="column"
      gap="xxs"
    >
      <OverflowTooltip overflowRef={overflowRef} tooltipProps={{ title: text }}>
        <Text
          className={redoTableCellsCss.overflowableText}
          fontSize={styleToArgs.mainFontSize}
          fontWeight={fontWeight ?? styleToArgs.mainFontWeight}
          ref={setOverflowRef}
          textColor={textColor ?? styleToArgs.mainTextColor}
        >
          {text}
        </Text>
      </OverflowTooltip>
      {subtext && (
        <OverflowTooltip
          overflowRef={subtextOverflowRef}
          tooltipProps={{ title: subtext }}
        >
          <Text
            className={redoTableCellsCss.overflowableText}
            fontSize={styleToArgs.subFontSize}
            ref={setSubtextOverflowRef}
            textColor={textColor ?? styleToArgs.subTextColor}
          >
            {subtext}
          </Text>
        </OverflowTooltip>
      )}
      {subtext2 && (
        <OverflowTooltip
          overflowRef={subtext2OverflowRef}
          tooltipProps={{ title: subtext2 }}
        >
          <Text
            className={redoTableCellsCss.overflowableText}
            fontSize={styleToArgs.subFontSize}
            ref={setSubtext2OverflowRef}
            textColor={textColor ?? styleToArgs.subTextColor}
          >
            {subtext2}
          </Text>
        </OverflowTooltip>
      )}
    </Flex>
  );
});

interface IRedoTableTextArrayCellProps<T> {
  items: T[] | undefined;
  formatItem?: (item: T) => string;
  emptyText?: string;
}

export const RedoTableTextArrayCell = <T,>({
  items,
  formatItem,
  emptyText = "---",
}: IRedoTableTextArrayCellProps<T>) => {
  if (!items || items.length === 0) {
    return (
      <RedoTableTextCell
        data-arbiter="RedoTableTextArrayCell"
        text={emptyText}
      />
    );
  }

  const formatItemText = (item: T) => {
    return formatItem ? formatItem(item) : String(item);
  };

  const [firstItem] = items;
  if (items.length === 1 && firstItem !== undefined) {
    return (
      <RedoTableTextCell
        data-arbiter="RedoTableTextArrayCell"
        text={formatItemText(firstItem)}
      />
    );
  }

  return (
    <RedoTablePaddedCell data-arbiter="RedoTableTextArrayCell">
      <ShadcnTooltip>
        <TooltipTrigger onClick={(e) => e.stopPropagation()}>
          <RedoBadge size="sm" text={`+${items.length}`} />
        </TooltipTrigger>
        <TooltipContent className="p-0" onClick={(e) => e.stopPropagation()}>
          {items.map((item, index) => (
            <div key={index}>
              <div className="p-2">
                <Text as="span">{formatItemText(item)}</Text>
              </div>
              {index < items.length - 1 && <Divider />}
            </div>
          ))}
        </TooltipContent>
      </ShadcnTooltip>
    </RedoTablePaddedCell>
  );
};

export const RedoTableFeaturedIconTextCell = memo(
  function RedoTableFeaturedIconTextCell({
    text,
    subtext,
    subtext2,
    leading,
    textColor,
    icon,
  }: FeaturedIconTextCellProps) {
    const styleToArgs = isLeadingToArgsMap(leading, subtext);
    const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
    const [subtitleoverflowRef, setSubtitleOverflowRef] =
      useState<HTMLDivElement | null>(null);
    return (
      <Flex
        align="center"
        className={classNames(
          redoTableCellsCss.cellContainer,
          redoTableCellsCss.featuredIconCellContainer,
        )}
        data-arbiter="RedoTableFeaturedIconTextCell"
        gap="sm"
      >
        <Flex className={redoTableCellsCss.featuredIconWrapper}>
          <RedoFeaturedIcon
            color={icon.color}
            Icon={() => <Text>{icon.text}</Text>}
            shape={icon.shape}
            size={icon.size}
          />
        </Flex>
        <Flex
          className={classNames(
            redoTableCellsCss.overflowableTextContainer,
            redoTableCellsCss.featuredIconTextContainer,
          )}
          dir="column"
          gap="sm"
        >
          <OverflowTooltip
            hideOnHover
            overflowRef={overflowRef}
            tooltipProps={{ title: text }}
          >
            <Text
              className={redoTableCellsCss.overflowableText}
              fontSize={styleToArgs.mainFontSize}
              fontWeight={styleToArgs.mainFontWeight}
              ref={setOverflowRef}
              textColor={textColor ?? styleToArgs.mainTextColor}
            >
              {text}
            </Text>
          </OverflowTooltip>
          {subtext && (
            <OverflowTooltip
              hideOnHover
              overflowRef={subtitleoverflowRef}
              tooltipProps={{ title: subtext }}
            >
              <Text
                className={redoTableCellsCss.overflowableText}
                fontSize={styleToArgs.subFontSize}
                ref={setSubtitleOverflowRef}
                textColor={textColor ?? styleToArgs.subTextColor}
              >
                {subtext}
              </Text>
            </OverflowTooltip>
          )}
          {subtext2 && (
            <Text
              className={redoTableCellsCss.overflowableText}
              fontSize={styleToArgs.subFontSize}
              textColor={textColor ?? styleToArgs.subTextColor}
            >
              {subtext2}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  },
);

const isLeadingToArgsMap = (leading?: boolean, subtext?: string): any => {
  if (leading) {
    return {
      mainFontSize: "xs",
      mainFontWeight: "medium",
      mainTextColor: "primary",
      subFontSize: "xxs",
      subFontWeight: "regular",
      subTextColor: "secondary",
    };
  }
  return {
    mainFontSize: "xs",
    mainFontWeight: "regular",
    mainTextColor: subtext ? "primary" : "secondary",
    subFontSize: "xxs",
    subFontWeight: "regular",
    subTextColor: "secondary",
  };
};

export function RedoTableCheckboxCell({
  selected,
  disabled,
  setSelected,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  selected: CheckboxValue;
  disabled?: boolean;
  setSelected?: (selected: CheckboxValue) => void;
}) {
  const stopClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };
  return (
    <Flex
      align="center"
      as="label"
      className={classNames(
        redoTableCellsCss.rowCheckboxCell,
        { [redoTableCellsCss.disabled ?? ""]: disabled },
        redoTableCellsCss.blackBorderOnHover,
      )}
      data-arbiter="RedoTableCheckboxCell"
      data-dd-action-name={actionName}
      justify="center"
      onClick={stopClick}
      p="xl"
    >
      <RedoCheckbox
        disabled={disabled}
        setValue={(value) => {
          setSelected?.(value);
        }}
        size="sm"
        value={selected}
      />
    </Flex>
  );
}

export const RedoTableCommandMenuCell = memo(function RedoTableCommandMenuCell({
  items,
  dataTestId,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  items: RedoCommandMenuItem[];
  dataTestId?: string;
}) {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const context = useContext(CommandMenuContext)!;
  const menuId = useMemo(() => new ObjectId().toString(), []);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const menuOpen = context.openMenuId === menuId;

  return (
    <>
      <Flex
        data-arbiter="RedoTableCommandMenuCell"
        data-dd-action-name={actionName}
        px="3xl"
      >
        <RedoButton
          aria-label={t("More options", { context: "table menu" })}
          className={redoTableRowCss.hoverable}
          dataTestId={dataTestId}
          IconLeading={DotsHorizontal}
          onClick={(e) => {
            e.stopPropagation();
            context.setOpenMenuId(menuOpen ? null : menuId);
          }}
          ref={setMenuAnchor}
        />
      </Flex>
      <RedoCommandMenu
        anchor={menuAnchor}
        items={items}
        open={menuOpen}
        setOpen={(open) => context.setOpenMenuId(open ? menuId : null)}
      />
    </>
  );
});

export const RedoTableToolbarCell = memo(function RedoTableButtonCell({
  buttons,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  buttons: Omit<RedoButtonProps, "size">[];
}) {
  return (
    <Flex
      align="center"
      data-arbiter="RedoTableToolbarCell"
      data-dd-action-name={actionName}
      justify="center"
      p="xl"
    >
      {buttons.map((button, idx) => (
        <RedoButton
          {...button}
          className={redoTableRowCss.hoverable}
          key={idx}
          size="sm"
        />
      ))}
    </Flex>
  );
});

interface IRedoTableBadgeArrayCellProps {
  items?: RedoBadgeProps[];
  emptyText?: string;
  CustomContextTooltip?: React.ComponentType<{ item: RedoBadgeProps }>;
}
/** A cell that displays a list of badges, with a +N badge if is more than 1 badge.
 * When hovering over the +N badge it will show a tooltip with all the badges in a list.
 **/
export const RedoTableBadgeArrayCell = ({
  items,
  emptyText = "---",
  CustomContextTooltip,
}: IRedoTableBadgeArrayCellProps) => {
  if (!items || items.length === 0) {
    return (
      <RedoTableTextCell
        data-arbiter="RedoTableBadgeArrayCell"
        text={emptyText}
      />
    );
  }

  if (items.length === 1) {
    return (
      <RedoTablePaddedCell data-arbiter="RedoTableBadgeArrayCell">
        <RedoBadge {...items[0]} />
      </RedoTablePaddedCell>
    );
  }

  return (
    <RedoTablePaddedCell data-arbiter="RedoTableBadgeArrayCell">
      <ShadcnTooltip>
        <TooltipTrigger onClick={(e) => e.stopPropagation()}>
          <RedoBadge size="sm" text={`+${items.length}`} />
        </TooltipTrigger>
        <TooltipContent className="p-0" onClick={(e) => e.stopPropagation()}>
          {items.map((item, index) =>
            CustomContextTooltip ? (
              <CustomContextTooltip item={item} key={index} />
            ) : (
              <div key={index}>
                <div className="p-2">
                  <RedoBadge {...item} />
                </div>
                {index < items.length - 1 && <Divider />}
              </div>
            ),
          )}
        </TooltipContent>
      </ShadcnTooltip>
    </RedoTablePaddedCell>
  );
};

/** A cell that displays a list of tags, with a +N badge if there are more tags than can fit.
 * Designed to be used with RedoTableWidthMode.FIXED columns.
 **/
export const RedoTableBadgeCell = memo(function RedoTableBadgeCell({
  badges,
  cellProps,
}: {
  badges: RedoBadgeProps[];
  cellProps?: { padding?: SpacingValue };
}) {
  const [cellRef, setCellRef] = useState<HTMLHeadingElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(badges.length);

  useLayoutEffect(() => {
    setVisibleCount(badges.length);
  }, [badges]);

  // After badges change, render all of them, measure each in DOM, then compute
  // the cutoff in one pass and update state exactly once. Avoids the
  // setState-in-effect feedback loop the previous trim-one-at-a-time approach
  // had on shrink-to-fit parents.
  useLayoutEffect(() => {
    if (!cellRef) {
      return;
    }
    const parent = cellRef.parentElement;
    if (!parent) {
      return;
    }

    const maxWidth = parent.getBoundingClientRect().width;
    const fullWidth = cellRef.getBoundingClientRect().width;

    if (fullWidth <= maxWidth) {
      setVisibleCount(badges.length);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const children = Array.from(cellRef.children) as HTMLElement[];
    let used = 0;
    let count = 0;
    for (let i = 0; i < badges.length && i < children.length; i++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const w = children[i].offsetWidth;
      if (used + w > maxWidth) {
        break;
      }
      used += w;
      count++;
    }
    setVisibleCount(count);
  }, [badges, cellRef]);

  const visibleBadges = badges.slice(0, visibleCount);
  const hiddenBadges = badges.slice(visibleCount);

  return (
    <Flex
      data-arbiter="RedoTableBadgeCell"
      p={cellProps?.padding ?? "xl"}
      ref={setCellRef}
    >
      {visibleBadges.map((tag, idx) => (
        <RedoBadge
          {...tag}
          color={tag.color || "blueLight"}
          key={typeof tag.text === "string" ? `${idx}-${tag.text}` : idx}
        />
      ))}
      {hiddenBadges.length > 0 && (
        <Tooltip title={hiddenBadges.map((badge) => badge.text).join(", ")}>
          {/* This div is needed to make the tooltip work */}
          <div>
            <RedoBadge color="gray" text={`+${hiddenBadges.length}`} />
          </div>
        </Tooltip>
      )}
    </Flex>
  );
});

export function RedoTableLoadingCell({
  size = "xs",
}: {
  size?: RedoSpinnerSize;
}) {
  return (
    <Flex
      className={classNames(
        redoTableCellsCss.overflowableTextContainer,
        redoTableCellsCss.cellContainer,
      )}
      data-arbiter="RedoTableLoadingCell"
      dir="column"
      gap="xxs"
    >
      <RedoSpinner size={size} />
    </Flex>
  );
}

export function RedoTableSkeletonTextCell({
  length = 6,
}: { length?: number } = {}) {
  return (
    <Flex
      className={classNames(
        redoTableCellsCss.overflowableTextContainer,
        redoTableCellsCss.cellContainer,
      )}
      data-arbiter="RedoTableSkeletonTextCell"
      dir="column"
      gap="xxs"
    >
      <SkeletonText length={length} />
    </Flex>
  );
}

export function RedoTablePaddedCell({
  children,
  "data-arbiter": dataArbiter = "RedoTablePaddedCell",
}: {
  children?: React.ReactNode;
  "data-arbiter"?: string;
}) {
  return (
    <Flex data-arbiter={dataArbiter} flex={1} p="xl">
      {children}
    </Flex>
  );
}
